import { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faEdit, faTrash, faMapMarkerAlt, faCalendar, 
  faDollarSign, faTag, faExpand,
  faComment, faHistory, faPaperclip, faInfoCircle, faMagic, faUpload, faDownload,
  faImage, faVideo, faFileAlt, faFileWord, faFileExcel, faFolder, faBox, faSync, faScroll,
  faChartLine, faSpinner, faExclamationTriangle, faClock, faUser, faStore,
  faSquare, faCheckSquare, faSave, faBan, faLock, faUnlockAlt, faKey, faRulerCombined
} from '@fortawesome/free-solid-svg-icons';
import { api, aiAPI, resolveFileUrl } from '../services/api';
import useI18n from '../hooks/useI18n';
import { formatQuantity } from '../config/unitOfMeasure';
import ImageViewer from './ImageViewer';
import PasswordLockModal from './PasswordLockModal';
import '../styles/ItemDetailView.css';

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface CommentEdit {
  editedAt: string;
  previousContent: string;
}

interface Comment {
  id: string;
  content: string;
  createdAt: string;
  isEdited?: boolean;
  editHistory?: CommentEdit[];
  user: {
    id: string;
    name: string;
    email: string;
  };
}

interface ItemFile {
  id: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
}

interface HistoryEntry {
  id: string;
  action: string;
  fieldName?: string;
  oldValue?: string;
  newValue?: string;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

interface ValuePrediction {
  estimatedValue: number;
  confidence: number;
  currency: string;
  basis: string;
}

interface Item {
  id: string;
  name: string;
  description?: string;
  categoryId?: string;
  category?: Category;
  condition: string;
  location?: string;
  purchasePlace?: string;
  purchasePrice?: number;
  purchaseCurrency?: string;
  purchaseDate?: string;
  purchaseTime?: string;
  purchaseTimezone?: string;
  predictedValue?: number;
  imageUrl?: string;
  images?: Array<{ id: string; url: string; isPrimary?: boolean; order?: number }>;
  isFavorite?: boolean;
  isLocked?: boolean;
  tags?: string[];
  createdAt: string;
  updatedAt?: string;
}

interface ItemDetailViewProps {
  item: Item;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (item: Item) => void;
  onDelete: (itemId: string) => void;
  onToggleFavorite: (itemId: string) => void;
}

const ItemDetailView = ({ item, isOpen, onClose, onEdit, onDelete, onToggleFavorite }: ItemDetailViewProps) => {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'details' | 'files' | 'comments' | 'history'>('details');
  const [imageFullscreen, setImageFullscreen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  const [closing, setClosing] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [postingComment, setPostingComment] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const [showCommentHistory, setShowCommentHistory] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Files state
  const [files, setFiles] = useState<ItemFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  // History state
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // AI Prediction state
  const [prediction, setPrediction] = useState<ValuePrediction | null>(null);
  const [predictingValue, setPredictingValue] = useState(false);
  const [predictionError, setPredictionError] = useState('');

  // Password lock state
  const [isItemLocked, setIsItemLocked] = useState(item.isLocked || false);
  const [lockModalOpen, setLockModalOpen] = useState(false);
  const [lockModalMode, setLockModalMode] = useState<'lock' | 'unlock' | 'verify' | 'change-password' | 'remove-lock'>('lock');

  // Sync lock state when item changes
  useEffect(() => {
    setIsItemLocked(item.isLocked || false);
  }, [item.id, item.isLocked]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => {
      setClosing(false);
      onClose();
    }, 200);
  }, [onClose]);

  // Fetch comments when switching to comments tab
  useEffect(() => {
    if (isOpen && activeTab === 'comments') {
      fetchComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, item.id]);

  // Fetch files when switching to files tab
  useEffect(() => {
    if (isOpen && activeTab === 'files') {
      fetchFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, item.id]);

  // Fetch history when switching to history tab
  useEffect(() => {
    if (isOpen && activeTab === 'history') {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, item.id]);

  // Reset tab when opening
  useEffect(() => {
    if (isOpen) {
      setActiveTab('details');
      setClosing(false);
      setPrediction(null);
      setPredictionError('');
      setEditingCommentId(null);
      setShowCommentHistory(null);
      setSelectedFiles(new Set());
    }
  }, [isOpen, item.id]);

  // Fetch current user ID
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/user/profile');
        setCurrentUserId(response.data.id || response.data.user?.id);
      } catch (error) {
        console.error('Failed to fetch current user:', error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Lock body scroll to avoid layout shift when modal opens
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousPadding = document.body.style.paddingRight;
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollBarWidth > 0) {
      document.body.style.paddingRight = `${scrollBarWidth}px`;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
    };
  }, [isOpen]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await api.get(`/items/${item.id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const fetchFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await api.get(`/items/${item.id}/files`);
      const data = Array.isArray(response.data) ? response.data : [];
      setFiles(data.map(normalizeFile));
    } catch (error) {
      console.error('Failed to fetch files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const normalizeFile = (file: any): ItemFile => ({
    id: file.id,
    fileName: file.originalName || file.fileName || file.filename || 'File',
    fileUrl: file.url || file.fileUrl,
    fileSize: file.size ?? file.fileSize ?? 0,
    fileType: file.mimeType || file.fileType || '',
    uploadedAt: file.createdAt || file.uploadedAt || new Date().toISOString(),
  });

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await api.get(`/audit/timeline/${item.id}`);
      const data = Array.isArray(response.data) ? response.data : [];
      setHistory(data);
    } catch (error: any) {
      try {
        const response = await api.get(`/audit/item/${item.id}`);
        const data = Array.isArray(response.data) ? response.data : (response.data?.logs || []);
        setHistory(data);
      } catch {
        console.error('Failed to fetch history:', error);
        setHistory([]);
      }
    } finally {
      setLoadingHistory(false);
    }
  };

  const handlePredictValue = async () => {
    setPredictingValue(true);
    setPredictionError('');
    try {
      const response = await aiAPI.predictValue(item.id);
      if (response.data.prediction) {
        setPrediction(response.data.prediction);
      } else if (response.data.estimatedValue) {
        setPrediction(response.data);
      } else {
        setPredictionError('Not enough data to predict value. Add a purchase price or category for better results.');
      }
    } catch (error: any) {
      setPredictionError(error?.response?.data?.error || 'Failed to predict value. Try again later.');
    } finally {
      setPredictingValue(false);
    }
  };

  // Password lock handlers
  const openLockModal = (mode: 'lock' | 'unlock' | 'change-password' | 'remove-lock') => {
    setLockModalMode(mode);
    setLockModalOpen(true);
  };

  const handleLockSuccess = (newLockedState: boolean) => {
    setIsItemLocked(newLockedState);
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;
    
    setPostingComment(true);
    try {
      const response = await api.post(`/items/${item.id}/comments`, {
        content: newComment.trim()
      });
      setComments([response.data, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error('Failed to post comment:', error);
    } finally {
      setPostingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await api.delete(`/items/${item.id}/comments/${commentId}`);
      setComments(comments.filter(c => c.id !== commentId));
    } catch (error) {
      console.error('Failed to delete comment:', error);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editingCommentContent.trim()) return;
    try {
      const response = await api.put(`/items/${item.id}/comments/${commentId}`, {
        content: editingCommentContent.trim()
      });
      setComments(comments.map(c => c.id === commentId ? { ...c, ...response.data, isEdited: true } : c));
      setEditingCommentId(null);
      setEditingCommentContent('');
    } catch (error) {
      console.error('Failed to edit comment:', error);
    }
  };

  const startEditingComment = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingCommentContent(comment.content);
  };

  const cancelEditingComment = () => {
    setEditingCommentId(null);
    setEditingCommentContent('');
  };

  const toggleFileSelection = (fileId: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(fileId)) {
      newSelection.delete(fileId);
    } else {
      newSelection.add(fileId);
    }
    setSelectedFiles(newSelection);
  };

  const selectAllFiles = () => {
    if (selectedFiles.size === files.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(files.map(f => f.id)));
    }
  };

  const handleDownloadSelectedFiles = async () => {
    const filesToDownload = files.filter(f => selectedFiles.has(f.id));
    for (const file of filesToDownload) {
      await handleDownloadFile(file.fileUrl, file.fileName);
      // Small delay between downloads
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    setSelectedFiles(new Set());
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/items/${item.id}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFiles([normalizeFile(response.data), ...files]);
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await api.delete(`/items/${item.id}/files/${fileId}`);
      setFiles(files.filter(f => f.id !== fileId));
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const formatFileSize = (bytes: number | undefined | null) => {
    if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) return 'Unknown size';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileType = (mimeType: string | undefined) => {
    if (!mimeType) return 'File';
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.startsWith('video/')) return 'Video';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Document';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Spreadsheet';
    return 'File';
  };

  const handleDownloadFile = async (fileUrl: string, fileName: string) => {
    try {
      const url = resolveFileUrl(fileUrl) || fileUrl;
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      // Fallback - open in new tab
      window.open(resolveFileUrl(fileUrl) || fileUrl, '_blank');
    }
  };

  const getFileIcon = (mimeType: string | undefined) => {
    if (!mimeType) return <FontAwesomeIcon icon={faFileAlt} />;
    if (mimeType.startsWith('image/')) return <FontAwesomeIcon icon={faImage} />;
    if (mimeType.startsWith('video/')) return <FontAwesomeIcon icon={faVideo} />;
    if (mimeType.includes('pdf')) return <FontAwesomeIcon icon={faFileAlt} />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FontAwesomeIcon icon={faFileWord} />;
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return <FontAwesomeIcon icon={faFileExcel} />;
    return <FontAwesomeIcon icon={faFileAlt} />;
  };

  if (!isOpen) return null;

  const formatCondition = (condition: string) => {
    const conditions: Record<string, { label: string; color: string }> = {
      'NEW': { label: t('itemDetail.conditionNew'), color: '#10b981' },
      'LIKE_NEW': { label: t('itemDetail.conditionLikeNew'), color: '#22c55e' },
      'EXCELLENT': { label: t('itemDetail.conditionExcellent'), color: '#84cc16' },
      'GOOD': { label: t('itemDetail.conditionGood'), color: '#eab308' },
      'FAIR': { label: t('itemDetail.conditionFair'), color: '#f97316' },
      'POOR': { label: t('itemDetail.conditionPoor'), color: '#ef4444' },
      'N/A': { label: t('itemDetail.conditionNA'), color: '#6b7280' },
      '': { label: t('itemDetail.notSet'), color: '#6b7280' },
    };
    return conditions[condition] || { label: condition || t('itemDetail.notSet'), color: '#6b7280' };
  };

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 0.8) return { text: t('itemDetail.highConfidence'), color: '#10b981' };
    if (confidence >= 0.5) return { text: t('itemDetail.mediumConfidence'), color: '#f59e0b' };
    return { text: t('itemDetail.lowConfidence'), color: '#ef4444' };
  };

  const getBasisLabel = (basis: string) => {
    switch (basis) {
      case 'purchase_price_with_condition': return t('itemDetail.basisPurchase');
      case 'category_average': return t('itemDetail.basisCategory');
      default: return t('itemDetail.basisAI');
    }
  };

  const getHistoryIcon = (action: string) => {
    switch (action) {
      case 'created': return faClock;
      case 'updated': return faEdit;
      case 'deleted': return faTrash;
      default: return faHistory;
    }
  };

  const getHistoryColor = (action: string) => {
    switch (action) {
      case 'created': return '#10b981';
      case 'updated': return '#667eea';
      case 'deleted': return '#ef4444';
      default: return '#a0aec0';
    }
  };

  const conditionInfo = formatCondition(item.condition);
  const imageUrls = (
    item.images && item.images.length > 0
      ? [...item.images]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((img) => resolveFileUrl(img.url))
          .filter((url): url is string => !!url)
      : item.imageUrl
        ? [resolveFileUrl(item.imageUrl)].filter((url): url is string => !!url)
        : []
  );

  const imageUrl = imageUrls[0] || null;

  return (
    <div className={`item-detail-overlay ${closing ? 'closing' : ''}`} onClick={handleClose}>
      <div
        className={`item-detail-modal ${closing ? 'closing' : ''}`}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Image Section */}
        <div className="item-detail-image-section">
          {imageUrl ? (
            <>
              <img 
                src={imageUrl} 
                alt={item.name} 
                className="item-detail-image"
                onClick={() => {
                  setImageIndex(0);
                  setImageFullscreen(true);
                }}
              />
              <button 
                className="image-expand-btn"
                onClick={() => {
                  setImageIndex(0);
                  setImageFullscreen(true);
                }}
                title={t('itemDetail.viewFullSize')}
              >
                <FontAwesomeIcon icon={faExpand} />
              </button>
              {imageUrls.length > 1 && (
                <div className="image-count-badge">
                  {imageUrls.length} {t('itemDetail.photos')}
                </div>
              )}
            </>
          ) : (
            <div className="item-detail-no-image">
              <span><FontAwesomeIcon icon={faBox} /></span>
              <p>{t('itemDetail.noImage')}</p>
            </div>
          )}
          
          {/* Favorite Button */}
          <button 
            className={`favorite-btn ${item.isFavorite ? 'is-active' : ''}`}
            onClick={() => onToggleFavorite(item.id)}
            title={item.isFavorite ? t('itemDetail.removeFromFavorites') : t('itemDetail.addToFavorites')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="heart-icon">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </div>

        {/* Content Section */}
        <div className="item-detail-content">
          {/* Header */}
          <div className="item-detail-header">
            <div>
              <h2>{item.name}</h2>
              {item.createdAt && (
                <span className="item-created-date">{t('itemDetail.added')} {formatDate(item.createdAt)}</span>
              )}
            </div>
            <button className="close-btn" onClick={handleClose}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Badges */}
          <div className="item-detail-badges">
            {item.category ? (
              <span 
                className="badge category-badge"
              >
                <FontAwesomeIcon icon={faFolder} /> {item.category.name}
              </span>
            ) : (
              <span className="badge category-badge">
                <FontAwesomeIcon icon={faFolder} /> {t('itemDetail.uncategorized')}
              </span>
            )}
            {item.condition && item.condition !== 'N/A' && (
              <span 
                className="badge condition-badge"
              >
                {conditionInfo.label}
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="item-detail-tabs">
            <button 
              className={`tab ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              <FontAwesomeIcon icon={faInfoCircle} /> {t('itemDetail.info')}
            </button>
            <button 
              className={`tab ${activeTab === 'files' ? 'active' : ''}`}
              onClick={() => setActiveTab('files')}
            >
              <FontAwesomeIcon icon={faPaperclip} /> {t('itemDetail.files')}
            </button>
            <button 
              className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              <FontAwesomeIcon icon={faComment} /> {t('itemDetail.comments')}
            </button>
            <button 
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <FontAwesomeIcon icon={faHistory} /> {t('itemDetail.log')}
            </button>
          </div>

          {/* Tab Content */}
          <div className="item-detail-tab-content">
            {activeTab === 'details' && (
              <div className="details-content">
                {/* Description */}
                <div className="detail-section">
                  <h4><FontAwesomeIcon icon={faInfoCircle} /> {t('itemDetail.description')}</h4>
                  <p>{item.description || t('itemDetail.noDescription')}</p>
                </div>

                {/* Info Grid - always show all fields */}
                <div className="detail-grid">
                  <div className="detail-card">
                    <div className="detail-icon location">
                      <FontAwesomeIcon icon={faMapMarkerAlt} />
                    </div>
                    <div>
                      <span className="detail-label">{t('itemDetail.location')}</span>
                      <span className="detail-value">{item.location || t('itemDetail.notSet')}</span>
                    </div>
                  </div>

                  <div className="detail-card">
                    <div className="detail-icon location">
                      <FontAwesomeIcon icon={faStore} />
                    </div>
                    <div>
                      <span className="detail-label">{t('itemDetail.purchasePlace')}</span>
                      <span className="detail-value">{item.purchasePlace || t('itemDetail.notSet')}</span>
                    </div>
                  </div>
                  
                  <div className="detail-card">
                    <div className="detail-icon date">
                      <FontAwesomeIcon icon={faCalendar} />
                    </div>
                    <div>
                      <span className="detail-label">{t('itemDetail.purchased')}</span>
                      <span className="detail-value">
                        {item.purchaseDate ? formatDate(item.purchaseDate) : t('itemDetail.notSet')}
                        {item.purchaseTime && (
                          <span className="detail-subvalue">{item.purchaseTime}</span>
                        )}
                        {item.purchaseTimezone && item.purchaseDate && (
                          <span className="timezone-badge">{item.purchaseTimezone}</span>
                        )}
                      </span>
                    </div>
                  </div>
                  
                  <div className="detail-card">
                    <div className="detail-icon price">
                      <FontAwesomeIcon icon={faDollarSign} />
                    </div>
                    <div>
                      <span className="detail-label">{t('itemDetail.purchasePrice')}</span>
                      <span className="detail-value">
                        {item.purchasePrice 
                          ? formatCurrency(item.purchasePrice, item.purchaseCurrency)
                          : t('itemDetail.notSet')
                        }
                      </span>
                    </div>
                  </div>
                  
                  <div className="detail-card">
                    <div className="detail-icon value">
                      <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div>
                      <span className="detail-label">{t('itemDetail.condition')}</span>
                      <span className="detail-value" style={{ color: conditionInfo.color }}>
                        {conditionInfo.label}
                      </span>
                    </div>
                  </div>

                  {(item as any).unitType && (
                    <div className="detail-card">
                      <div className="detail-icon unit">
                        <FontAwesomeIcon icon={faRulerCombined} />
                      </div>
                      <div>
                        <span className="detail-label">{t('unit.title')}</span>
                        <span className="detail-value">
                          {(item as any).unitQuantity 
                            ? formatQuantity((item as any).unitQuantity, (item as any).unitSubtype)
                            : t(`unit.type.${(item as any).unitType}` as any)
                          }
                          {(item as any).unitSubtype && (item as any).unitQuantity && (
                            <span className="detail-subvalue">
                              {t(`unit.type.${(item as any).unitType}` as any)}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                  <div className="detail-section">
                    <h4><FontAwesomeIcon icon={faTag} /> {t('itemDetail.tags')}</h4>
                    <div className="tags-list">
                      {item.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Value Prediction - Functional */}
                <div className="ai-section">
                  <h4><FontAwesomeIcon icon={faMagic} /> {t('itemDetail.aiPrediction')}</h4>
                  
                  {prediction ? (
                    <div className="ai-prediction-result">
                      <div className="ai-prediction-main">
                        <span className="ai-value">
                          {formatCurrency(prediction.estimatedValue, prediction.currency)}
                        </span>
                        <span className="ai-confidence" style={{ color: getConfidenceLabel(prediction.confidence).color }}>
                          {getConfidenceLabel(prediction.confidence).text}
                        </span>
                      </div>
                      <p className="ai-basis">{getBasisLabel(prediction.basis)}</p>
                      <button className="refresh-btn" onClick={handlePredictValue} disabled={predictingValue}>
                        <FontAwesomeIcon icon={faSync} spin={predictingValue} />
                      </button>
                    </div>
                  ) : item.predictedValue ? (
                    <div className="ai-prediction-result">
                      <div className="ai-prediction-main">
                        <span className="ai-value">
                          {formatCurrency(item.predictedValue, item.purchaseCurrency)}
                        </span>
                        <span className="ai-confidence" style={{ color: '#a0aec0' }}>
                          {t('itemDetail.previousPrediction')}
                        </span>
                      </div>
                      <button className="refresh-btn" onClick={handlePredictValue} disabled={predictingValue}>
                        <FontAwesomeIcon icon={faSync} spin={predictingValue} /> {t('itemDetail.recalculate')}
                      </button>
                    </div>
                  ) : (
                    <div className="ai-prediction-empty">
                      {predictingValue ? (
                        <div className="ai-loading-state">
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>{t('itemDetail.analyzing')}</span>
                        </div>
                      ) : predictionError ? (
                        <div className="ai-error">
                          <FontAwesomeIcon icon={faExclamationTriangle} />
                          <span>{predictionError}</span>
                          <button className="btn-retry" onClick={handlePredictValue}>{t('itemDetail.tryAgain')}</button>
                        </div>
                      ) : (
                        <>
                          <p>{t('itemDetail.aiEstimateDesc')}</p>
                          <button className="btn-predict" onClick={handlePredictValue}>
                            <FontAwesomeIcon icon={faMagic} /> {t('itemDetail.predictValue')}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Item Meta */}
                <div className="detail-section meta-section">
                  <h4><FontAwesomeIcon icon={faClock} /> {t('itemDetail.itemInfo')}</h4>
                  <div className="meta-grid">
                    <div className="meta-item">
                      <span className="meta-label">{t('itemDetail.created')}</span>
                      <span className="meta-value">{formatDateTime(item.createdAt)}</span>
                    </div>
                    {item.updatedAt && (
                      <div className="meta-item">
                        <span className="meta-label">{t('itemDetail.lastUpdated')}</span>
                        <span className="meta-value">{formatDateTime(item.updatedAt)}</span>
                      </div>
                    )}
                    <div className="meta-item">
                      <span className="meta-label">{t('itemDetail.itemId')}</span>
                      <span className="meta-value id-value" title={item.id}>{item.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'files' && (
              <div className="files-content">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                  style={{ display: 'none' }}
                />
                <div className="files-header">
                  <button 
                    className="upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingFile}
                  >
                    <FontAwesomeIcon icon={faUpload} />
                    {uploadingFile ? t('itemDetail.uploading') : t('itemDetail.uploadFile')}
                  </button>
                  {files.length > 0 && (
                    <div className="files-selection-actions">
                      <button
                        className={`select-all-btn ${selectedFiles.size === files.length ? 'active' : ''}`}
                        onClick={selectAllFiles}
                      >
                        <FontAwesomeIcon icon={selectedFiles.size === files.length ? faCheckSquare : faSquare} />
                        {selectedFiles.size === files.length ? t('itemDetail.deselectAll') : t('itemDetail.selectAll')}
                      </button>
                      {selectedFiles.size > 0 && (
                        <button
                          className="download-selected-btn"
                          onClick={handleDownloadSelectedFiles}
                        >
                          <FontAwesomeIcon icon={faDownload} />
                          {t('itemDetail.downloadSelected').replace('{count}', String(selectedFiles.size))}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                
                {loadingFiles ? (
                  <div className="loading-state">{t('itemDetail.loadingFiles')}</div>
                ) : files.length > 0 ? (
                  <div className="files-list">
                    {files.map(file => (
                      <div key={file.id} className={`file-item ${selectedFiles.has(file.id) ? 'selected' : ''}`}>
                        <button 
                          className="file-checkbox"
                          onClick={() => toggleFileSelection(file.id)}
                        >
                          <FontAwesomeIcon icon={selectedFiles.has(file.id) ? faCheckSquare : faSquare} />
                        </button>
                        <span className="file-icon">{getFileIcon(file.fileType)}</span>
                        <div className="file-info">
                          <span className="file-name">{file.fileName}</span>
                          <span className="file-meta">
                            {getFileType(file.fileType)} • {formatFileSize(file.fileSize)} • Added {formatDateTime(file.uploadedAt)}
                          </span>
                        </div>
                        <div className="file-actions">
                          <button 
                            className="file-btn download"
                            onClick={() => handleDownloadFile(file.fileUrl, file.fileName)}
                            title="Download"
                          >
                            <FontAwesomeIcon icon={faDownload} />
                          </button>
                          <button 
                            className="file-btn delete"
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <span><FontAwesomeIcon icon={faPaperclip} /></span>
                    <p>{t('itemDetail.noFiles')}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'comments' && (
              <div className="comments-content">
                <div className="comment-input-section">
                  <div className="comment-input">
                    <div className="user-avatar">U</div>
                    <textarea 
                      placeholder={t('itemDetail.addComment')} 
                      rows={2}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handlePostComment();
                        }
                      }}
                    />
                  </div>
                  <button 
                    className="post-btn"
                    onClick={handlePostComment}
                    disabled={postingComment || !newComment.trim()}
                  >
                    <FontAwesomeIcon icon={faComment} /> 
                    {postingComment ? t('itemDetail.posting') : t('itemDetail.post')}
                  </button>
                </div>
                
                {loadingComments ? (
                  <div className="loading-state">{t('itemDetail.loadingComments')}</div>
                ) : comments.length > 0 ? (
                  <div className="comments-list">
                    {comments.map(comment => (
                      <div key={comment.id} className={`comment-item ${editingCommentId === comment.id ? 'editing' : ''}`}>
                        <div className="comment-avatar">
                          {comment.user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="comment-body">
                          <div className="comment-header">
                            <span className="comment-author">{comment.user.name || comment.user.email}</span>
                            <span className="comment-date">
                              {new Date(comment.createdAt).toLocaleDateString()}
                              {comment.isEdited && (
                                <span 
                                  className="edited-badge"
                                  onClick={() => setShowCommentHistory(showCommentHistory === comment.id ? null : comment.id)}
                                  title="Click to view edit history"
                                >
                                  {t('itemDetail.edited')}
                                </span>
                              )}
                            </span>
                          </div>
                          
                          {editingCommentId === comment.id ? (
                            <div className="comment-edit-form">
                              <textarea
                                value={editingCommentContent}
                                onChange={(e) => setEditingCommentContent(e.target.value)}
                                rows={3}
                                autoFocus
                              />
                              <div className="comment-edit-actions">
                                <button 
                                  className="btn-save"
                                  onClick={() => handleEditComment(comment.id)}
                                  disabled={!editingCommentContent.trim()}
                                >
                                  <FontAwesomeIcon icon={faSave} /> {t('itemDetail.save')}
                                </button>
                                <button 
                                  className="btn-cancel"
                                  onClick={cancelEditingComment}
                                >
                                  <FontAwesomeIcon icon={faBan} /> {t('itemDetail.cancel')}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <p className="comment-text">{comment.content}</p>
                          )}
                          
                          {/* Edit History Panel */}
                          {showCommentHistory === comment.id && comment.editHistory && comment.editHistory.length > 0 && (
                            <div className="comment-history-panel">
                              <h5>{t('itemDetail.editHistory')}</h5>
                              {comment.editHistory.map((edit, idx) => (
                                <div key={idx} className="history-entry">
                                  <span className="history-time">
                                    {new Date(edit.editedAt).toLocaleString()}
                                  </span>
                                  <span className="history-previous">
                                    "{edit.previousContent.length > 100 ? edit.previousContent.slice(0, 100) + '...' : edit.previousContent}"
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        
                        {/* Comment Actions */}
                        <div className="comment-actions">
                          {(currentUserId === comment.user.id) && editingCommentId !== comment.id && (
                            <button 
                              className="comment-edit-btn"
                              onClick={() => startEditingComment(comment)}
                              title="Edit comment"
                            >
                              <FontAwesomeIcon icon={faEdit} />
                            </button>
                          )}
                          {(currentUserId === comment.user.id) && (
                            <button 
                              className="comment-delete-btn"
                              onClick={() => handleDeleteComment(comment.id)}
                              title="Delete comment"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <span><FontAwesomeIcon icon={faComment} /></span>
                    <p>{t('itemDetail.noComments')}</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-content">
                {loadingHistory ? (
                  <div className="loading-state">
                    <FontAwesomeIcon icon={faSpinner} spin /> {t('itemDetail.loadingHistory')}
                  </div>
                ) : history.length > 0 ? (
                  <div className="history-timeline">
                    {history.map((entry) => (
                      <div key={entry.id} className="history-item">
                        <div className="history-dot" style={{ backgroundColor: getHistoryColor(entry.action) }}>
                          <FontAwesomeIcon icon={getHistoryIcon(entry.action)} />
                        </div>
                        <div className="history-content-item">
                          <div className="history-header">
                            <span className="history-action" style={{ color: getHistoryColor(entry.action) }}>
                              {entry.action === 'created' ? t('itemDetail.itemCreated') :
                               entry.action === 'updated' ? t('itemDetail.updatedField').replace('{field}', entry.fieldName || 'item') :
                               entry.action === 'deleted' ? t('itemDetail.itemDeleted') : entry.action}
                            </span>
                            <span className="history-date">{formatDateTime(entry.createdAt)}</span>
                          </div>
                          {entry.fieldName && entry.action === 'updated' && (
                            <div className="history-changes">
                              {entry.oldValue && (
                                <span className="history-old">
                                  {entry.oldValue.length > 50 ? entry.oldValue.slice(0, 50) + '...' : entry.oldValue}
                                </span>
                              )}
                              {entry.oldValue && entry.newValue && <span className="history-arrow">→</span>}
                              {entry.newValue && (
                                <span className="history-new">
                                  {entry.newValue.length > 50 ? entry.newValue.slice(0, 50) + '...' : entry.newValue}
                                </span>
                              )}
                            </div>
                          )}
                          {entry.user && (
                            <span className="history-user">
                              <FontAwesomeIcon icon={faUser} /> {entry.user.name || entry.user.email}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <span><FontAwesomeIcon icon={faScroll} /></span>
                    <p>{t('itemDetail.noHistory')}</p>
                    <p className="empty-hint">{t('itemDetail.changesTracked')}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="item-detail-actions">
            {isItemLocked ? (
              <>
                <button className="action-btn lock-active" onClick={() => openLockModal('unlock')}>
                  <FontAwesomeIcon icon={faLock} /> {t('itemDetail.locked')}
                </button>
                <button className="action-btn change-lock" onClick={() => openLockModal('change-password')}>
                  <FontAwesomeIcon icon={faKey} /> {t('itemDetail.changePin')}
                </button>
                <button className="action-btn remove-lock" onClick={() => openLockModal('remove-lock')}>
                  <FontAwesomeIcon icon={faUnlockAlt} /> {t('itemDetail.removeLock') || 'Remove Lock'}
                </button>
              </>
            ) : (
              <button className="action-btn lock" onClick={() => openLockModal('lock')}>
                <FontAwesomeIcon icon={faUnlockAlt} /> {t('itemDetail.lockItem')}
              </button>
            )}
            <button className="action-btn edit" onClick={() => onEdit(item)}>
              <FontAwesomeIcon icon={faEdit} /> {t('itemDetail.edit')}
            </button>
            <button className="action-btn delete" onClick={() => onDelete(item.id)}>
              <FontAwesomeIcon icon={faTrash} /> {t('itemDetail.delete')}
            </button>
          </div>
        </div>

        {/* Fullscreen Image Modal */}
        {imageFullscreen && imageUrls.length > 0 && (
          <ImageViewer
            images={imageUrls}
            currentIndex={imageIndex}
            onClose={() => {
              setImageFullscreen(false);
            }}
            onNext={() => setImageIndex((prev) => (prev + 1) % imageUrls.length)}
            onPrev={() => setImageIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length)}
            onIndexChange={setImageIndex}
          />
        )}

        {/* Password Lock Modal */}
        {lockModalOpen && (
          <PasswordLockModal
            itemId={item.id}
            itemName={item.name}
            isLocked={isItemLocked}
            mode={lockModalMode}
            onClose={() => setLockModalOpen(false)}
            onSuccess={handleLockSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default ItemDetailView;
